import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  actionCloseClockOutFlow,
  actionSetEmbeddedLaborTicket,
  actionOpenLaborTicketModal,
  actionOpenShiftHandover,
} from 'lib/actions';
import { getClockOutFlowOpen } from 'lib/selectors/getClockOutFlowOpen';
import {
  MMEmbeddableZone,
  useEmbeddedEffect,
} from '@machinemetrics/mm-react-embeddable';
import { LABOR_TICKET_HOST } from 'lib/appConfig';
import i18n from 'i18next';
import { getLaborTicketModalOpen } from 'lib/selectors/getLaborTicketModalOpen';
import { getLatestOperator } from 'lib/selectors/getLatestOperator';
import { getEnableOperatorVerification } from 'lib/selectors/getEnableOperatorVerification';
import { getClockOutOperator } from 'lib/selectors/getClockOutOperator';
import { getShiftHandoverEnabled } from 'lib/selectors/getShiftHandoverEnabled';

const EmbeddedLaborTicket = ({ id, operatorForClockOut }) => {
  const dispatch = useDispatch();
  const enableOperatorVerification = useSelector(getEnableOperatorVerification);
  const shiftHandoverEnabled = useSelector(getShiftHandoverEnabled);

  // Handle manual close (user clicks cancel/close button)
  useEmbeddedEffect(id, 'labor-ticket-screen:close', () => {
    dispatch(actionCloseClockOutFlow());
  });

  // Handle when all labor tickets are completed
  // The labor ticket embed emits this when there are no more tickets to process
  useEmbeddedEffect(id, 'labor-tickets:no-tickets', () => {
    // If shift handover is enabled, close this embed and transition to it
    // Otherwise, keep the embed open - it will show the clock-out button
    if (shiftHandoverEnabled) {
      dispatch(actionCloseClockOutFlow());
      dispatch(actionOpenShiftHandover());
    }
  });

  useEmbeddedEffect(id, 'labor-ticket-screen:open', (data) => {
    dispatch(actionSetEmbeddedLaborTicket(data.ticket));
    dispatch(actionOpenLaborTicketModal());
  });

  return (
    <MMEmbeddableZone
      id={id}
      src={`${LABOR_TICKET_HOST}`}
      path="/"
      context={{
        personId: operatorForClockOut,
        enableOperatorVerification,
      }}
      params={{
        language: i18n.language,
      }}
    />
  );
};

export const ClockOutEmbedWrapper = () => {
  const id = 'labor-ticket-screen';
  const clockOutFlowOpen = useSelector(getClockOutFlowOpen);
  const laborTicketModalOpen = useSelector(getLaborTicketModalOpen);
  const latestOperator = useSelector(getLatestOperator);
  const clockOutOperator = useSelector(getClockOutOperator);

  if (!clockOutFlowOpen || laborTicketModalOpen) {
    return null;
  }
  // Use the stored clockOutOperator if available, otherwise fall back to latestOperator's erpId
  const operatorForClockOut = clockOutOperator || latestOperator?.erpId;
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
        zIndex: 1000,
      }}
    >
      <EmbeddedLaborTicket id={id} operatorForClockOut={operatorForClockOut} />
    </div>
  );
};
